import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { expensesApi, expenseKeys } from '../api';
import type {
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseFilters,
  ExpenseStatus,
} from '../types';

export function useExpenses(filters: ExpenseFilters) {
  return useQuery({
    queryKey: expenseKeys.list(filters),
    queryFn: () => expensesApi.getExpenses(filters),
    staleTime: 60 * 1000,
  });
}

export function useExpenseMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateExpenseDto) => expensesApi.createExpense(data),
    onSuccess: () => {
      toast.success(t('expense.createSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('expense.createError'))),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateExpenseDto }) =>
      expensesApi.updateExpense(id, data),
    onSuccess: () => {
      toast.success(t('expense.updateSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('expense.updateError'))),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string | number; status: ExpenseStatus }) =>
      expensesApi.setStatus(id, status),
    onSuccess: () => {
      toast.success(t('expense.statusSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('expense.statusError'))),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => expensesApi.deleteExpense(id),
    onSuccess: () => {
      toast.success(t('expense.deleteSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('expense.deleteError'))),
  });

  return { createMutation, updateMutation, statusMutation, deleteMutation };
}
